import { useModal } from "@/hooks/modals/useModal";
import { convertFileItems } from "@/utils/file";
import { useEffect, useState } from "react";
import { SourcingNodeConfig } from "../type";
import { useSourcingBy } from "./useSourcingBy";
import { useSourcingRequirementQuery } from "./useSourcingRequirementQuery";

export function useSourcingFlowNodesFormBehavior(
  defaultSourcingNodes: SourcingNodeConfig[],
  currentSourcingMaterialId: string,
) {
  const { props } = useModal("SOURCING_APPLICATION_20251202");
  const { recordId } = props;
  const detail = useSourcingRequirementQuery(recordId);

  const { isSourcingBy } = useSourcingBy(recordId);

  const [sourcingNodes, setSourcingNodes] = useState(defaultSourcingNodes);

  useEffect(() => {
    if (!detail) return;

    const isProductionMaterial = detail.sourcingType === "1";

    const targetMaterial = detail.srmSourcingMaterialList.find(
      (m) => m.id.toString() === currentSourcingMaterialId,
    );

    if (!targetMaterial) return;

    const { sourcingNodeList } = targetMaterial;

    sourcingNodeList.forEach((node) => {
      if (node.nodeCode !== "M1" || node.nodeStatus !== "1") return;

      if (detail.ursFiles && !isProductionMaterial) {
        node.relatedFiles = convertFileItems(detail.ursFiles)
          .map((f) => f.url)
          .join();
      }

      if (detail.standardMaterialFiles && isProductionMaterial) {
        node.relatedFiles = convertFileItems(detail.standardMaterialFiles)
          .map((f) => f.url)
          .join();
      }
      if (detail.newStandardFiles && isProductionMaterial) {
        node.newRelatedFiles = convertFileItems(detail.newStandardFiles)
          .map((f) => f.url)
          .join();
      }
    });

    // 将详情数据中的节点信息同步到配置项中
    setSourcingNodes((prevNodes) => {
      const newNodes = prevNodes.map((node) => {
        sourcingNodeList.forEach((n) => {
          if (node.nodeCode === n.nodeCode) {
            node.extra = {
              id: n.id,
              nodeRemarks: n.nodeRemarks,
              relatedFiles: n.relatedFiles,
              newRelatedFiles: n.newRelatedFiles,
              isCompleted: n.nodeStatus === "2",
              supplierId: n.supplierId ? n.supplierId.split(",") : null,
              supplierList: n.supplierList,
            };
          }
        });
        return node;
      });

      return newNodes;
    });
  }, [detail, currentSourcingMaterialId]);

  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(
    () => new Set([]),
  );

  const toggleCollapse = (nodeCode: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(nodeCode)) {
        next.delete(nodeCode);
      } else {
        next.add(nodeCode);
      }
      return next;
    });
  };

  const expandAll = () => setCollapsedKeys(new Set());
  const collapseAll = () =>
    setCollapsedKeys(new Set(sourcingNodes.map((n) => n.nodeCode)));

  return {
    sourcingNodes,
    isSourcingBy,
    collapsedKeys,
    toggleCollapse,
    expandAll,
    collapseAll,
  };
}
